import React, { useState, useEffect, useRef } from 'react';
import { baseGrade, cubeText, creativeLookGrade } from '../utils/grade';
import { lightroomPresetXmp } from '../utils/lightroom';
import SITE from '../site';

// Guided "replicate the pro workflow" panel for the photo editor. It mirrors the
// exact path a photographer takes on a RED frame: pull the flat negative →
// de-log to a neutral base (display transform) → finish with a creative grade —
// and lets you PREVIEW each stage in-app and DOWNLOAD the full-resolution image
// for each stage (16-bit TIFF or JPEG). The tiny .cube LUT is offered as a
// secondary "recipe" file (rebuild the look in Resolve / Premiere / Photoshop) —
// it is NOT the image, which is why it's small.

// The three full-resolution export snapshots along the pipeline. These line up
// with the guided workflow's phases: Negative = before any edit, Neutral base =
// the Decode step output, Creative = the finished grade. `jump` points at the
// guided step that produces each snapshot so "Edit this stage" lands you there.
const STAGES = [
  {
    id: 'negative',
    step: '1',
    name: 'Negative (as shot)',
    desc:
      'The flat 16-bit RED log / Wide-Gamut frame — the most RAW-like file with the FULL highlight & shadow latitude and NO exposure baked in. Best handoff if you want to set exposure & grade from scratch in Lightroom.',
    cube: false,
    jump: 'decode',
  },
  {
    id: 'neutral',
    step: '2',
    name: 'Neutral base',
    desc:
      'De-logged to a clean Rec.709/sRGB base via the Display Transform (IPP2). Looks normal immediately, but the display exposure + tone-map ARE baked here — this is the "Decode" step of the guided workflow.',
    cube: true,
    jump: 'decode',
  },
  {
    id: 'final',
    step: '3',
    name: 'Creative look',
    desc: 'Your in-app grade (white balance, tone, HSL, wheels…) — the finished frame from all 10 guided steps.',
    cube: true,
    jump: 'effects',
  },
];

// Per-stage image formats the user can download. TIFF is the pro/print/Lightroom
// handoff; JPEG is the quick share.
const FORMATS = [
  { id: 'tiff16', label: '16-bit TIFF' },
  { id: 'fulljpeg', label: 'JPEG' },
];

function triggerDownload(url) {
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  a.target = '_blank';
  a.rel = 'noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function downloadText(text, filename, mime = 'text/plain') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.replace(/[^\w.-]+/g, '_');
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// The look/LUT handoff files. Each targets a different app + input, so they're
// labeled by WHERE they go and WHAT they expect as input.
const LOOK_EXPORTS = [
  {
    id: 'xmp',
    label: 'Lightroom preset (.xmp)',
    primary: true,
    note: 'The creative look as Lightroom sliders. Set exposure & white balance per clip first, then apply.',
    make: (grade) => downloadText(lightroomPresetXmp(grade, SITE.lookName), `${SITE.id}-look.xmp`, 'application/rdf+xml'),
  },
  {
    id: 'look',
    label: 'Look LUT (.cube)',
    note: 'Apply ON TOP of the Neutral base (Photoshop Color Lookup, Resolve, etc.). Exposure/WB-neutral.',
    make: (grade) => downloadText(cubeText(creativeLookGrade(grade), 33, SITE.lookName), `${SITE.id}-look.cube`),
  },
  {
    id: 'full',
    label: 'Full creative LUT (.cube)',
    note: 'The whole pipeline (de-log → look). Apply to the ORIGINAL RED log clip in Resolve/Premiere.',
    make: (grade) => downloadText(cubeText(grade, 33, `${SITE.lookName} Full`), `${SITE.id}-look-full.cube`),
  },
  {
    id: 'neutral',
    label: 'Neutral base LUT (.cube)',
    note: 'Log → Rec.709/sRGB de-log only (no look). The conversion the Neutral base uses.',
    make: (grade) => downloadText(cubeText(baseGrade(grade), 33, `${SITE.lookGroup} Neutral Base`), `${SITE.id}-neutral-base.cube`),
  },
];

export default function ColorWorkflow({
  grade,
  currentStageId,
  onJumpToStep,
  onExportStages,
  stageDerivatives = {},
  baking = false,
}) {
  // Track which {stage:format} downloads the user kicked off so we can show a
  // "Preparing…" state and auto-download the file the moment the bake lands. We
  // remember the URL at request time so a *fresh* bake (new URL) triggers the
  // save even if a stale artifact already existed.
  const [pending, setPending] = useState({}); // key -> url-at-request (string)
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  useEffect(() => {
    const next = { ...pendingRef.current };
    let changed = false;
    for (const key of Object.keys(pendingRef.current)) {
      const [id, fmt] = key.split(':');
      const url = stageDerivatives[id]?.[fmt];
      if (url && url !== pendingRef.current[key]) {
        triggerDownload(url);
        delete next[key];
        changed = true;
      }
    }
    if (changed) setPending(next);
  }, [stageDerivatives]);

  const requestDownload = (id, fmt) => {
    const existing = stageDerivatives[id]?.[fmt] || '';
    setPending((p) => ({ ...p, [`${id}:${fmt}`]: existing }));
    onExportStages([{ id, formats: [fmt] }]);
  };

  return (
    <div className="cs-cw">
      <div className="cs-cw-head">
        <span className="cs-cw-title">Export &amp; handoff</span>
        <span className="cs-cw-sub">Download any pipeline snapshot at full resolution</span>
      </div>

      {STAGES.map((s) => {
        const dl = stageDerivatives[s.id] || {};
        const here = onJumpToStep && currentStageId === s.jump;
        return (
          <div key={s.id} className="cs-cw-stage">
            <div className="cs-cw-stage-top">
              <span className="cs-cw-step">{s.step}</span>
              <span className="cs-cw-name">{s.name}</span>
              {onJumpToStep && (
                <button
                  type="button"
                  className={`cs-btn cs-btn-sm ${here ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                  onClick={() => onJumpToStep(s.jump)}
                  title="Jump the guided workflow to the step that produces this snapshot"
                >
                  {here ? 'Editing' : 'Edit stage'}
                </button>
              )}
            </div>
            <div className="cs-cw-desc">{s.desc}</div>
            <div className="cs-cw-dl">
              <span className="cs-cw-dl-label">Download image:</span>
              <div className="cs-cw-dl-row">
                {FORMATS.map((f, i) => {
                  const key = `${s.id}:${f.id}`;
                  const isPending = key in pending;
                  const url = dl[f.id];
                  return (
                    <button
                      key={f.id}
                      type="button"
                      className={`cs-btn cs-btn-sm ${i === 0 ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                      disabled={isPending || (baking && !isPending)}
                      onClick={() => (url && !isPending ? triggerDownload(url) : requestDownload(s.id, f.id))}
                      title={`Bake & download the full-resolution ${f.label} for this stage (crop applied)`}
                    >
                      {isPending ? 'Preparing…' : `↓ ${f.label}`}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      <div className="cs-cw-exports">
        <div className="cs-cw-exports-head">Take the look elsewhere</div>
        {LOOK_EXPORTS.map((x) => (
          <div key={x.id} className="cs-cw-export-row">
            <button
              type="button"
              className={`cs-btn cs-btn-sm ${x.primary ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => x.make(grade)}
              title={x.note}
            >
              ↓ {x.label}
            </button>
            <span className="cs-cw-export-note">{x.note}</span>
          </div>
        ))}
      </div>

      <div className="cs-cw-foot">
        <b>Image</b> downloads (TIFF/JPEG) render full-resolution on the server with your crop
        applied, then save automatically. <b>LUT/preset</b> files are the look recipe (tiny). The
        recommended Lightroom path: download the <b>Neutral base</b> image, calibrate exposure &amp;
        white balance for that clip, then apply the <b>Lightroom preset</b>.
      </div>
    </div>
  );
}
